import { buildGeetestSendCodePayload, buildGraphCodeSendCodePayload } from '../utils/login-sms'

const PHONE_LOGIN_VERSION = 124
const PHONE_APPLICATION_TYPE = 7

export const usePhoneLoginTab = () => {
  const appStore = useAppStore()
  const route = useRoute()
  const matomo = useMatomo()
  const { invokeV3 } = useLoginApi()
  const { accessCookie } = useLogin()
  const { ensureSelectedCountry } = useLoginCountries()
  const { t } = useAppI18n()

  const currentCountry = computed(() => appStore.usergetCountry)
  const loading = ref(false)
  const countDown = ref(0)
  const isCountdown = ref(false)
  const showGeetest = ref(false)
  const showGraphical = ref(false)
  let timer: ReturnType<typeof setTimeout> | null = null

  const form = reactive({
    phone: '',
    smsCode: '',
    requestId: '',
    phoneError: '',
    smsError: '',
    figuresmsError: '',
  })

  const btnEnabled = computed(() => Boolean(form.phone && form.smsCode))

  const resetErrors = () => {
    form.phoneError = ''
    form.smsError = ''
    form.figuresmsError = ''
  }

  const reset = () => {
    form.phone = ''
    form.smsCode = ''
    form.requestId = ''
    resetErrors()
    countDown.value = 0
    isCountdown.value = false
    showGeetest.value = false
    showGraphical.value = false
    loading.value = false
    if (timer) clearTimeout(timer)
  }

  const startCount = () => {
    if (timer) clearTimeout(timer)
    if (countDown.value < 1) {
      isCountdown.value = false
      return
    }
    timer = setTimeout(() => {
      countDown.value -= 1
      startCount()
    }, 1000)
  }

  const validatePhone = () => {
    if (currentCountry.value.code === '0086') {
      if (!/^[1][3-9][0-9]{9}$/.test(form.phone)) {
        form.phoneError = t('84005')
        return false
      }
    } else if (!/^.{5,20}$/.test(form.phone)) {
      form.phoneError = t('84005')
      return false
    }

    form.phoneError = ''
    return true
  }

  const sendCodeRequest = async (payload: Record<string, any>) => {
    const result = await invokeV3('usercenter$sendcodegeettest4', payload).catch(() => null)

    const node = result?.usercenter?.sendcodegeettest4
    if (!node?.success) {
      const message = node?.extra || t('84051')
      if (showGraphical.value) {
        form.figuresmsError = message
      } else {
        form.smsError = message
      }
      matomo.trackEvent('af_phone_vercode_get', {
        af_judge: '否',
        af_type: message,
        af_content: `${currentCountry.value.code}+${form.phone}`,
      })
      return false
    }

    form.smsError = ''
    form.figuresmsError = ''
    form.requestId = node.data?.requestid || ''
    countDown.value = 60
    isCountdown.value = true
    showGeetest.value = false
    showGraphical.value = false
    matomo.trackEvent('af_phone_vercode_get', {
      af_judge: '是',
      af_type: '',
      af_content: `${currentCountry.value.code}+${form.phone}`,
    })
    startCount()
    return true
  }

  const sendCode = async () => {
    if (loading.value || isCountdown.value) return
    if (!validatePhone()) return

    resetErrors()
    if (route.query.type === 'register') {
      const verified = await verifyThirdPartyPhone()
      if (!verified) return
    }
    showGeetest.value = true
  }

  const handleGeetestSuccess = async (payload: Record<string, any>) => {
    await sendCodeRequest(
      buildGeetestSendCodePayload(
        {
          areaCode: currentCountry.value.code,
          phone: form.phone,
          languageCode: appStore.LanguageCode,
          msgType: 0,
          smsBusinessType: 1,
          applicationType: PHONE_APPLICATION_TYPE,
        },
        payload as any,
      ),
    )
  }

  const handleGeetestError = () => {
    showGeetest.value = false
    showGraphical.value = true
  }

  const handleGraphicalSubmit = async (graphCode: string) => {
    await sendCodeRequest(
      buildGraphCodeSendCodePayload(
        {
          areaCode: currentCountry.value.code,
          phone: form.phone,
          languageCode: appStore.LanguageCode,
          msgType: 0,
          smsBusinessType: 1,
          applicationType: PHONE_APPLICATION_TYPE,
        },
        graphCode,
      ),
    )
  }

  const closeCaptchaLayers = () => {
    showGeetest.value = false
    showGraphical.value = false
  }

  const phoneLogin = async () => {
    const result = await invokeV3('usercenter$newusercenterphonequickloginv2', {
      areaCode: currentCountry.value.code,
      phone: form.phone,
      msgCode: form.smsCode,
      requestId: form.requestId,
      version: PHONE_LOGIN_VERSION,
      applicationType: PHONE_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      ip: appStore.userIp,
    }).catch(() => null)

    const node = result?.usercenter?.newusercenterphonequickloginv2
    if (!node?.success) {
      form.smsError = node?.extra || '登录失败，请稍后重试'
      return false
    }

    form.smsError = ''
    matomo.trackEvent('af_login_success', { af_type: '手机号' })
    await accessCookie(node.data)
    return true
  }

  const phoneRegister = async () => {
    const result = await invokeV3('usercenter$phoneregisterv3', {
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.phone,
      deviceInformation: 'web',
      version: PHONE_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.smsCode,
      registrationType: 112,
      applicationType: PHONE_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
    }).catch(() => null)

    const node = result?.usercenter?.phoneregisterv3
    if (!node?.success) {
      form.smsError = node?.extra || '注册失败，请稍后重试'
      return false
    }

    form.smsError = ''
    matomo.trackEvent('af_login_success', { af_type: '手机号' })
    await accessCookie(node.data)
    return true
  }

  const verifyThirdPartyPhone = async () => {
    const result = await invokeV3('usercenter$thirdphoneverifyv3', {
      AreaCode: currentCountry.value.code,
      PhoneNubmer: form.phone,
      AccountType: String(route.query.logintype || ''),
    }).catch(() => null)

    const node = result?.usercenter?.thirdphoneverifyv3
    if (!node?.success) {
      form.phoneError = node?.extra || '手机号校验失败，请稍后重试'
      return false
    }

    form.phoneError = ''
    return true
  }

  const thirdPartyPhoneRegister = async () => {
    const result = await invokeV3('usercenter$thridphoneregisterv3', {
      openId: String(route.query.id || ''),
      accountNick: String(route.query.name || ''),
      accountHead: String(route.query.img || ''),
      accountType: String(route.query.logintype || ''),
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.phone,
      deviceInformation: 'web',
      version: PHONE_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.smsCode,
      registrationType: 112,
      applicationType: PHONE_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
    }).catch(() => null)

    const node = result?.usercenter?.thridphoneregisterv3
    if (!node?.success) {
      form.smsError = node?.extra || '注册失败，请稍后重试'
      return false
    }

    form.smsError = ''
    const loginTypeRaw = String(route.query.logintype || '').toLowerCase()
    let afType = '微信'
    if (loginTypeRaw.includes('google')) afType = '谷歌'
    else if (loginTypeRaw.includes('apple')) afType = 'apple id'
    else if (loginTypeRaw.includes('linkedin')) afType = '领英'
    else if (loginTypeRaw.includes('facebook')) afType = 'facebook'
    matomo.trackEvent('af_login_success', { af_type: afType })
    await accessCookie(node.data)
    return true
  }

  const submit = async () => {
    if (!btnEnabled.value || loading.value) return false
    if (!validatePhone()) return false
    if (!form.smsCode) {
      form.smsError = t('82403')
      return false
    }

    loading.value = true
    resetErrors()

    if (route.query.type === 'register') {
      const phoneVerified = await verifyThirdPartyPhone()
      const success = phoneVerified ? await thirdPartyPhoneRegister() : false
      loading.value = false
      return success
    }

    const result = await invokeV3('usercenter$newusercenterphoneisregisterv2', {
      areaCode: currentCountry.value.code,
      phoneNumber: form.phone,
    }).catch(() => null)

    const node = result?.usercenter?.newusercenterphoneisregisterv2
    if (!node?.success) {
      form.phoneError = node?.extra || '手机号校验失败，请稍后重试'
      loading.value = false
      return false
    }

    const success = node.data?.isregister ? await phoneLogin() : await phoneRegister()
    loading.value = false
    return success
  }

  onMounted(() => {
    ensureSelectedCountry()
  })

  onBeforeUnmount(() => {
    if (timer) clearTimeout(timer)
  })

  return {
    btnEnabled,
    countDown,
    currentCountry,
    form,
    handleGeetestError,
    handleGeetestSuccess,
    handleGraphicalSubmit,
    isCountdown,
    loading,
    reset,
    showGeetest,
    showGraphical,
    sendCode,
    submit,
    validatePhone,
    closeCaptchaLayers,
  }
}
